import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { toast } from 'sonner';

const LS_KEY = 'global_alerts_prefs_v1';

interface Prefs { enabled: boolean; sound: boolean; }

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { enabled: true, sound: true, ...JSON.parse(raw) };
  } catch {}
  return { enabled: true, sound: true };
}
function savePrefs(p: Prefs) { try { localStorage.setItem(LS_KEY, JSON.stringify(p)); } catch {} }

function playBeep(high = false) {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = high ? [880, 1320] : [660, 990];
    tones.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      const start = now + i * 0.18;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(start); o.stop(start + 0.36);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch {}
}

const HIGH_TYPES = new Set([
  'booking_cancelled', 'payout_rejected', 'application_rejected',
  'role_application_new', 'payout_request_new',
]);

/** Global per-user notifications listener: toast + sound for every role. */
export function useGlobalAlerts() {
  const { user } = useAuth();
  const { locale } = useLocale();
  const isAr = locale === 'ar';
  const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs());
  const prefsRef = useRef(prefs); prefsRef.current = prefs;

  const setEnabled = useCallback((enabled: boolean) => {
    setPrefs((p) => { const n = { ...p, enabled }; savePrefs(n); return n; });
  }, []);
  const setSound = useCallback((sound: boolean) => {
    setPrefs((p) => { const n = { ...p, sound }; savePrefs(n); if (sound) playBeep(true); return n; });
  }, []);

  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`global-alerts-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload: any) => {
        if (!prefsRef.current.enabled) return;
        const n = payload.new;
        const title = isAr ? n.title_ar : (n.title_en || n.title_ar);
        const body = isAr ? n.body_ar : (n.body_en || n.body_ar);
        const high = HIGH_TYPES.has(n.type);
        const fn: any = high ? (toast as any).error : (toast as any).info ?? toast;
        fn(title, { description: body });
        if (prefsRef.current.sound) playBeep(high);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, isAr]);

  return { prefs, setEnabled, setSound, testSound: () => playBeep(false) };
}
